import { useState, useEffect } from "react";
import { Building, Cog, LogOut, Sun, Moon, Monitor, Flame, UsersRound, History, Download } from "lucide-react";
import { usePWAInstall } from "@/hooks/usePWAInstall";
import { PWAInstallDialog } from "@/components/PWAInstallDialog";
import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Sidebar, SidebarContent, SidebarGroup, SidebarGroupContent, SidebarGroupLabel, SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from "@/components/ui/sidebar";
import { mainItems, emailItems, otherItems } from "@/components/sidebar/sidebarConfig";
import { useSidebarData } from "@/hooks/useSidebarData";

export function AppSidebar() {
  const { signOut } = useAuth();
  const { state } = useSidebar();
  const location = useLocation();
  const currentPath = location.pathname;
  const [theme, setTheme] = useState<'light' | 'dark' | 'system'>('system');

  const {
    userOrganization,
    organizationLogo,
    teamCode,
    isAdmin,
    isTeamLeader,
    openTodosCount,
    activeAreaHotLeadsCount,
    unreadEmailsCount
  } = useSidebarData();

  const { isIOS, isInstallable, promptInstall, canShowInstallOption } = usePWAInstall();
  const [showIOSDialog, setShowIOSDialog] = useState(false);

  const handleInstallClick = async () => {
    const result = await promptInstall();
    if (result === 'ios') {
      setShowIOSDialog(true);
    }
  };

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') as 'light' | 'dark' | 'system' || 'system';
    setTheme(savedTheme);
    applyTheme(savedTheme);
  }, []);

  const applyTheme = (newTheme: 'light' | 'dark' | 'system') => {
    const root = window.document.documentElement;
    if (newTheme === 'system') {
      const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
      root.classList.remove('light', 'dark');
      root.classList.add(systemTheme);
    } else {
      root.classList.remove('light', 'dark');
      root.classList.add(newTheme);
    }
  };

  const toggleTheme = () => {
    const themes: ('light' | 'dark' | 'system')[] = ['light', 'dark', 'system'];
    const currentIndex = themes.indexOf(theme);
    const nextTheme = themes[(currentIndex + 1) % themes.length];
    setTheme(nextTheme);
    localStorage.setItem('theme', nextTheme);
    applyTheme(nextTheme);
  };

  const getThemeIcon = () => {
    switch (theme) {
      case 'light': return Sun;
      case 'dark': return Moon;
      default: return Monitor;
    }
  };

  const isActive = (path: string) => currentPath === path;
  const isCollapsed = state === "collapsed";
  const isDarkMode = document.documentElement.classList.contains('dark');

  const getNavCls = ({ isActive }: { isActive: boolean }) => {
    if (isActive) {
      return "bg-sidebar-primary text-sidebar-primary-foreground font-medium";
    }
    return isDarkMode
      ? "text-slate-200 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
      : "text-slate-700 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground";
  };

  return (
    <Sidebar collapsible="icon">
      <SidebarContent>
        {/* Header */}
        <div className="p-4 border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {organizationLogo ? (
                <img
                  src={organizationLogo}
                  alt={userOrganization || "Logo"}
                  className="h-8 w-8 object-contain rounded-lg"
                  onError={(e) => {
                    console.log('Logo failed to load, falling back to placeholder');
                    e.currentTarget.style.display = 'none';
                  }}
                />
              ) : (
                <div className="h-8 w-8 bg-gradient-primary rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-sm">
                    {userOrganization ? userOrganization.charAt(0).toUpperCase() : '?'}
                  </span>
                </div>
              )}
              {!isCollapsed && <span className="text-lg font-bold">D2D+</span>}
            </div>
            {!isCollapsed && (
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleTheme}
                className="h-8 w-8 p-0"
                title={`Theme: ${theme}`}
              >
                {(() => {
                  const ThemeIcon = getThemeIcon();
                  return <ThemeIcon className="h-4 w-4" />;
                })()}
              </Button>
            )}
          </div>
        </div>

        {/* Teams Section */}
        {userOrganization && (
          <SidebarGroup>
            <SidebarGroupLabel>Team</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <div className="flex items-center px-2 py-2 min-h-8">
                    <Building className="mr-2 h-4 w-4 flex-shrink-0" />
                    {!isCollapsed && teamCode && (
                      <span className="text-xs text-muted-foreground font-mono">{teamCode}</span>
                    )}
                  </div>
                </SidebarMenuItem>
                {isTeamLeader && (
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild>
                      <NavLink to="/team" end className={({ isActive }) => getNavCls({ isActive })}>
                        <UsersRound className="mr-2 h-4 w-4" />
                        {!isCollapsed && <span className="text-sm">Team verwalten</span>}
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )}
                {isAdmin && (
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild>
                      <NavLink to="/organization" end className={({ isActive }) => getNavCls({ isActive })}>
                        <Cog className="mr-2 h-4 w-4" />
                        {!isCollapsed && <span className="text-sm">Organisation</span>}
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        {/* Main Navigation */}
        <SidebarGroup>
          <SidebarGroupLabel>Hauptmenü</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {mainItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild>
                    <NavLink to={item.url} end className={({ isActive }) => getNavCls({ isActive })}>
                      <item.icon className="mr-2 h-4 w-4" />
                      {!isCollapsed && (
                        <span className="flex items-center gap-2">
                          {item.title}
                          {item.title === "ToDos" && openTodosCount > 0 && (
                            <span className="bg-gradient-primary/20 text-primary border border-primary/30 px-2 py-0.5 rounded-full text-xs font-medium">
                              {openTodosCount}
                            </span>
                          )}
                          {item.title === "Laufliste" && activeAreaHotLeadsCount > 0 && (
                            <span className="bg-red-500/20 text-red-700 border border-red-500/30 px-2 py-0.5 rounded-full text-xs font-medium flex items-center gap-1">
                              <Flame className="h-3 w-3" />
                              {activeAreaHotLeadsCount}
                            </span>
                          )}
                        </span>
                      )}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Email Navigation */}
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {emailItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild>
                    <NavLink to={item.url} className={({ isActive }) => getNavCls({ isActive })}>
                      <item.icon className="mr-2 h-4 w-4" />
                      {!isCollapsed && (
                        <span className="flex items-center gap-2">
                          {item.title}
                          {item.title === "Eingang" && unreadEmailsCount > 0 && (
                            <span className="bg-gradient-primary/20 text-primary border border-primary/30 px-2 py-0.5 rounded-full text-xs font-medium">
                              {unreadEmailsCount}
                            </span>
                          )}
                        </span>
                      )}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Other Items */}
        <SidebarGroup>
          <SidebarGroupLabel>Weitere Optionen</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {otherItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild>
                    <NavLink to={item.url} end className={({ isActive }) => getNavCls({ isActive })}>
                      <item.icon className="mr-2 h-4 w-4" />
                      {!isCollapsed && <span>{item.title}</span>}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
              <SidebarMenuItem>
                <SidebarMenuButton asChild>
                  <NavLink to="/changelog" className={({ isActive }) => getNavCls({ isActive })}>
                    <History className="mr-2 h-4 w-4" />
                    {!isCollapsed && <span>Changelog</span>}
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
              {canShowInstallOption && (
                <SidebarMenuItem>
                  <SidebarMenuButton asChild>
                    <div 
                      onClick={handleInstallClick} 
                      className="flex items-center cursor-pointer text-primary hover:text-primary hover:bg-primary/10"
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => e.key === 'Enter' && handleInstallClick()}
                    >
                      <Download className="mr-2 h-4 w-4" />
                      {!isCollapsed && <span>App laden</span>}
                    </div>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
              <SidebarMenuItem>
                <SidebarMenuButton onClick={signOut} className="text-red-600 hover:text-red-700 hover:bg-red-50">
                  <LogOut className="mr-2 h-4 w-4" />
                  {!isCollapsed && <span>Abmelden</span>}
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      
      <PWAInstallDialog open={showIOSDialog} onOpenChange={setShowIOSDialog} />
    </Sidebar>
  );
}
